
import React, { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Rating,
  Stack,
  IconButton,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MailIcon from "@mui/icons-material/Mail";
import LanguageIcon from "@mui/icons-material/Language";
import CallIcon from "@mui/icons-material/Call";
import StarIcon from "@mui/icons-material/Star";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useNavigate } from "react-router-dom";
import { ASSET_BASE } from "../../utils/baseUrls";

export default function ProfessionalCard({
  name,
  location,
  email,
  website,
  phone,
  gst,
  established,
  id,
  images,
  rating,
  reviewCount,
  description,
  serviceid,
  userid,
  onViewShop,
  viewType = "grid" // Default to grid
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const isGrid = viewType === "grid";

  const getImageUrl = (img) => {
    if (!img) return "/assets/images/placeholder_img.png";
    const firstImg = Array.isArray(img) ? img[0] : img;
    if (!firstImg) return "/assets/images/placeholder_img.png";
    if (firstImg.startsWith("http")) return firstImg;
    return `${ASSET_BASE}storage/${firstImg}`;
  };

  const imageUrl = getImageUrl(images);

  const maskPhone = (phoneStr) => {
    if (!phoneStr) return "";
    const str = String(phoneStr).trim();
    if (str.length <= 4) return str;
    const first2 = str.slice(0, 2);
    const last2 = str.slice(-2);
    const middleMask = "X".repeat(str.length - 4);
    return `${first2}${middleMask}${last2}`;
  };

  const maskEmail = (emailStr) => {
    if (!emailStr) return "";
    const str = String(emailStr).trim();
    const atIndex = str.indexOf("@");
    if (atIndex === -1) {
      if (str.length <= 4) return str;
      return str.slice(0, 2) + "X".repeat(str.length - 4) + str.slice(-2);
    }
    const localPart = str.slice(0, atIndex);
    const domainPart = str.slice(atIndex);
    if (localPart.length <= 4) {
      return localPart + domainPart;
    }
    const first2 = localPart.slice(0, 2);
    const last2 = localPart.slice(-2);
    const middleMask = "X".repeat(localPart.length - 4);
    return `${first2}${middleMask}${last2}${domainPart}`;
  };

  return (
    <Card
      sx={{
        width: "100%",
        background: "#FBFAF7",
        borderRadius: isGrid ? 4 : 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        overflow: "hidden",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          "&:last-child": { pb: 2 }
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isGrid ? "column" : { xs: "column", sm: "row" },
            gap: isGrid ? 2 : 4,
          }}
        >
          {/* IMAGE */}
          <Box
            sx={{
              width: isGrid ? "100%" : { xs: "100%", sm: 220 },
              height: isGrid ? 200 : { xs: 200, sm: 220 },
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #95B160",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isGrid ? "#FBFAF7" : "#fff",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={name || "Professional"}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                p: images ? 1.5 : 3,
              }}
            />
          </Box>

          {/* CONTENT */}
          <Box
            display="flex"
            flexDirection="column"
            gap={isGrid ? 0.8 : 1}
            sx={{
              flex: 1,
              textAlign: "left",
              alignItems: "flex-start"
            }}
          >
            {/* TITLE */}
            <Typography
              variant={isGrid ? "subtitle1" : "h5"}
              fontWeight={700}
              sx={{
                width: "100%",
                wordBreak: "break-word",
                display: isGrid ? "-webkit-box" : "block",
                WebkitLineClamp: isGrid ? 2 : "none",
                WebkitBoxOrient: "vertical",
                overflow: isGrid ? "hidden" : "visible",
                minHeight: isGrid ? "2.8em" : "auto",
                lineHeight: 1.4
              }}
            >
              {name || "Professional Services"}
            </Typography>

            {/* Location (Pin + Text) */}
            {/* <Box display="flex" alignItems="flex-start" gap={0.8} sx={{ minHeight: isGrid ? "2.8em" : "auto" }}>
              <LocationOnIcon fontSize="small" sx={{ color: "#82A240", mt: 0.3 }} />
              <Typography 
                fontSize={14} 
                color="#666"
                sx={{
                  display: isGrid ? "-webkit-box" : "block",
                  WebkitLineClamp: isGrid ? 2 : "none",
                  WebkitBoxOrient: "vertical",
                  overflow: isGrid ? "hidden" : "visible",
                  lineHeight: 1.4,
                  width: "100%",
                  wordBreak: "break-word"
                }}
              >
                {location?.includes(",") 
                  ? location.split(",").slice(1).join(",").trim() 
                  : (location || "Chennai, Tamil Nadu, India")}
              </Typography>
            </Box> */}

            {/* Phone (Icon + Green Underlined) */}
            {phone && (
              <Box display="flex" alignItems="center" gap={0.8}>
                <CallIcon fontSize="small" sx={{ color: "#82A240" }} />
                <Typography
                  fontSize={14}
                  fontWeight={600}
                  sx={{
                    color: "#82A240",
                    textDecoration: "underline",
                    cursor: "pointer"
                  }}
                >
                  {maskPhone(phone)}
                </Typography>
              </Box>
            )}

            {/* RATING */}
            <Box display="flex" alignItems="center" gap={1}>
              <Rating
                value={rating || 0}
                precision={0.5}
                readOnly
                size="small"
                icon={<StarIcon fontSize="inherit" sx={{ color: "#FEB821" }} />}
              />
              <Typography fontSize={14} color="#666">
                {rating || "0.0"} ({reviewCount || 0} Reviews)
              </Typography>
            </Box>

            {!isGrid && (
              /* EMAIL + WEBSITE + INSTA - Only in List View */
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 4,
                  mt: 1,
                  mb: 1.5
                }}
              >
                <InfoItem icon={<MailIcon sx={{ fontSize: 18 }} />} text={maskEmail(email || "info@email.com")} />
                <InfoItem icon={<LanguageIcon sx={{ fontSize: 18 }} />} text={website || "website.com"} />
                {/* <IconButton size="small" sx={{ ml: "auto", bgcolor: "#FBAF24", color: "#fff", "&:hover": { bgcolor: "#e09a1f" }, p: 0.5 }}>
                  <InstagramIcon sx={{ fontSize: 18 }} />
                </IconButton> */}
              </Box>
            )}

            {/* GST + Established */}
            <Box sx={{
              mt: 0.5, display: "flex",
              flexDirection: isGrid ? "column" : "row",
              gap: isGrid ? "0.2rem" : "5.2rem"
            }}>
              <Typography fontSize={13} color="#666">
                GSTIN: {gst || "29ABCDE1234F1Z5"}
              </Typography>
              <Typography fontSize={13} color="#666">
                Established Year: {established || "1998"}
              </Typography>
            </Box>

            {/* View Shop Button */}
            <Button
              variant="contained"
              fullWidth={isGrid}
              sx={{
                mt: 1.5,
                background: "#82A240",
                color: "#fff",
                textTransform: "none",
                px: isGrid ? 2 : 3,
                borderRadius: "10px",
                fontWeight: 600,
                "&:hover": { background: "#6A8A35" },
                minWidth: isGrid ? "100%" : "130px",
                py: 0.8,
                alignSelf: isGrid ? "stretch" : "flex-end"
              }}
              onClick={(e) => {
                e.stopPropagation();
                onViewShop && onViewShop();
              }}>
              View Shop
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/* INFO ITEM */
const InfoItem = ({ icon, text }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Box sx={infoIcon}>{icon}</Box>
    <Typography fontSize={14} sx={{ color: "#6BAF4B", wordBreak: "break-all" }}>
      {text}
    </Typography>
  </Box>
);

/* STYLES */
const infoIcon = {
  boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
  borderRadius: "50%",
  p: 0.4,
  backgroundColor: "#FFFFFF",
  color: "#95B160",
};

const btnGreen = {
  background: "#7E9F40",
  color: "#fff",
  textTransform: "none",
  px: 3,
  borderRadius: "9px",
  "&:hover": { background: "#7E9F40" },
};

